import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Loader, XCircle } from 'lucide-react';

interface DeletionStatusResponse {
  status: string;
  message: string;
  rows_deleted?: number;
  created_at?: string;
}

const DataDeletionStatus: React.FC = () => {
  const [data, setData] = useState<DeletionStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (!id) {
      setError('No confirmation code provided.');
      setLoading(false);
      return;
    }
    fetch(`/api/oauth/facebook/data-deletion-status?id=${encodeURIComponent(id)}`)
      .then(r => r.json())
      .then((d: DeletionStatusResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError('Failed to load deletion status.'); setLoading(false); });
  }, []);

  const iconFor = (status: string) => {
    if (status === 'ok') return <CheckCircle size={40} style={{ color: 'var(--success, #16a34a)' }} />;
    if (status === 'not_found') return <AlertCircle size={40} style={{ color: 'var(--text-secondary)' }} />;
    if (status === 'error') return <XCircle size={40} style={{ color: '#dc2626' }} />;
    return <AlertCircle size={40} style={{ color: 'var(--text-secondary)' }} />;
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg, #f1f5f9)',
      padding: '20px',
    }}>
      <div style={{
        background: 'var(--panel, rgba(255,255,255,0.9))',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--stroke, rgba(15,23,42,0.10))',
        borderRadius: 'var(--radius-md, 16px)',
        padding: '40px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{ marginBottom: '16px', fontWeight: 700, fontSize: '20px', color: 'var(--text, #0f172a)' }}>
          AIME Data Deletion
        </div>

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: 'var(--text-secondary)' }}>
            <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <span>Looking up your request&hellip;</span>
          </div>
        )}

        {error && !loading && (
          <div style={{ color: '#dc2626', fontSize: '14px' }}>{error}</div>
        )}

        {data && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            {iconFor(data.status)}
            <p style={{ fontSize: '15px', color: 'var(--text, #0f172a)', margin: 0, lineHeight: 1.6 }}>
              {data.message}
            </p>
            {data.created_at && (
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                Request received: {new Date(data.created_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        <div style={{ marginTop: '28px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Questions? Contact{' '}
          <a href="mailto:support@clearbyte.au" style={{ color: 'var(--primary, #2177ff)' }}>
            support@clearbyte.au
          </a>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionStatus;
